import { getBlogArticle, type BlogSection } from "../lib/blog";
import { Link, useRoute } from "../lib/router";

export function BlogArticle() {
  const route = useRoute();
  const article = getBlogArticle(route);

  if (!article) {
    return (
      <section className="min-h-[58vh] py-20 px-4 text-center">
        <h1 className="text-3xl font-extrabold text-stone-900 dark:text-white mb-3">Article not found</h1>
        <p className="text-stone-500 dark:text-zinc-400 mb-8">The article you are looking for is not available.</p>
        <Link href="/blog" className="inline-flex px-5 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors">
          Back to Blog
        </Link>
      </section>
    );
  }

  return (
    <article className="py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Link href="/blog" className="text-sm font-semibold text-orange-600 dark:text-orange-400 hover:underline">
          Back to Blog
        </Link>
        <div className="mt-8 mb-10">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase text-orange-600 dark:text-orange-400 mb-5">
            <span>{article.primaryKeyword}</span>
            <span className="text-stone-300 dark:text-zinc-700">/</span>
            <span>Volume {article.volume}</span>
            <span className="text-stone-300 dark:text-zinc-700">/</span>
            <span>CPC {article.cpc}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white tracking-tight leading-tight mb-5">
            {article.title}
          </h1>
          <p className="text-lg text-stone-500 dark:text-zinc-400 leading-relaxed">
            {article.description}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs text-stone-400 dark:text-zinc-500">
            <span>{article.updated}</span>
            <span>{article.readTime}</span>
          </div>
        </div>

        <aside className="rounded-2xl border border-orange-200/60 dark:border-orange-500/20 bg-orange-50/70 dark:bg-orange-500/10 p-5 mb-10">
          <p className="text-xs font-bold uppercase text-orange-700 dark:text-orange-300 mb-2">Search opportunity</p>
          <p className="text-sm text-orange-900/80 dark:text-orange-100/80 leading-relaxed">{article.opportunity}</p>
          {article.secondaryKeywords && (
            <p className="text-xs text-orange-800/70 dark:text-orange-200/70 mt-3">
              Secondary keywords: {article.secondaryKeywords.join(", ")}
            </p>
          )}
        </aside>

        <div className="space-y-6">
          {article.sections.map((section, index) => (
            <ArticleSection key={index} section={section} />
          ))}
        </div>
      </div>
    </article>
  );
}

function ArticleSection({ section }: { section: BlogSection }) {
  if (section.kind === "h2") {
    return <h2 className="text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight pt-6">{section.text}</h2>;
  }

  if (section.kind === "h3") {
    return <h3 className="text-xl font-bold text-stone-900 dark:text-white tracking-tight pt-4">{section.text}</h3>;
  }

  if (section.kind === "p") {
    return <p className="text-stone-600 dark:text-zinc-300 leading-7">{section.text}</p>;
  }

  if (section.kind === "ul") {
    return (
      <ul className="space-y-2 pl-5 list-disc text-stone-600 dark:text-zinc-300 leading-7">
        {section.items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    );
  }

  if (section.kind === "ol") {
    return (
      <ol className="space-y-2 pl-5 list-decimal text-stone-600 dark:text-zinc-300 leading-7">
        {section.items.map((item) => <li key={item}>{item}</li>)}
      </ol>
    );
  }

  if (section.kind === "code") {
    return (
      <pre className="overflow-x-auto rounded-2xl bg-stone-950 p-5 text-sm text-stone-100 leading-6">
        <code>{section.code}</code>
      </pre>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-stone-200/60 dark:border-zinc-800/60">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 dark:bg-zinc-900">
          <tr>
            {section.headers.map((header) => (
              <th key={header} className="p-4 text-left font-semibold text-stone-900 dark:text-white">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100 dark:divide-zinc-800">
          {section.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="p-4 text-stone-600 dark:text-zinc-300 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

